import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { PETS, SMOKING, GUESTS } from '../../lib/listingConstants';

const RadioGrid = ({ options, value, onChange, testPrefix, cols = 2 }) => (
  <div className={`grid grid-cols-${cols} gap-2`}>
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        onClick={() => onChange(o.value)}
        className={`px-3 py-2.5 rounded-xl border text-sm font-medium ${
          value === o.value ? 'border-amber-500 bg-amber-50 text-amber-900' : 'border-slate-200 bg-white text-slate-700'
        }`}
        data-testid={`${testPrefix}-${o.value}`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const StepRules = ({ listing, patchDraft, onNext, saving }) => {
  const [pets, setPets] = useState(listing.pets_allowed || '');
  const [smoking, setSmoking] = useState(listing.smoking_allowed || '');
  const [guests, setGuests] = useState(listing.guests_allowed || '');
  const [qStart, setQStart] = useState(listing.quiet_hours_start || '');
  const [qEnd, setQEnd] = useState(listing.quiet_hours_end || '');
  const [extra, setExtra] = useState(listing.additional_house_rules || '');

  const canContinue = pets && smoking && guests;

  const save = async () => {
    await patchDraft({
      pets_allowed: pets,
      smoking_allowed: smoking,
      guests_allowed: guests,
      quiet_hours_start: qStart || null,
      quiet_hours_end: qEnd || null,
      additional_house_rules: extra || null,
    });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-extrabold text-slate-900">House rules</h1>
        <p className="mt-1 text-sm text-slate-600">Set expectations up front. Fewer surprises means happier tenants.</p>
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-semibold text-slate-900">Pets</Label>
        <RadioGrid options={PETS} value={pets} onChange={setPets} testPrefix="pets" cols={2} />
      </div>
      <div className="space-y-2">
        <Label className="text-sm font-semibold text-slate-900">Smoking</Label>
        <RadioGrid options={SMOKING} value={smoking} onChange={setSmoking} testPrefix="smoking" cols={3} />
      </div>
      <div className="space-y-2">
        <Label className="text-sm font-semibold text-slate-900">Overnight guests</Label>
        <RadioGrid options={GUESTS} value={guests} onChange={setGuests} testPrefix="guests" cols={1} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-sm font-semibold text-slate-900 mb-2 block">Quiet hours start</Label>
          <Input type="time" value={qStart} onChange={(e) => setQStart(e.target.value)} className="h-12" data-testid="quiet-start" />
        </div>
        <div>
          <Label className="text-sm font-semibold text-slate-900 mb-2 block">Quiet hours end</Label>
          <Input type="time" value={qEnd} onChange={(e) => setQEnd(e.target.value)} className="h-12" data-testid="quiet-end" />
        </div>
      </div>

      <div>
        <Label className="text-sm font-semibold text-slate-900 mb-2 block">Additional rules (optional)</Label>
        <textarea
          value={extra}
          onChange={(e) => setExtra(e.target.value)}
          maxLength={1000}
          rows={4}
          placeholder="e.g. Shoes off at the door. No subletting. Weekly cleaning rotation for shared spaces."
          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm resize-none"
          data-testid="additional-rules"
        />
        <p className="text-xs text-slate-500 mt-1">{extra.length}/1000</p>
      </div>

      <Button
        className="w-full btn-primary h-12 text-base"
        disabled={!canContinue || saving}
        onClick={save}
        data-testid="step-rules-continue"
      >
        Continue
      </Button>
    </div>
  );
};

export default StepRules;
